import { Container, Singleton } from 'typescript-ioc'
import { AppState } from '@halo/stores/app.state'
import { AppService } from '@halo/services/app.service'
import { IApiResponse, IFeed } from '@pushly/aqe/lib/interfaces/index'
import querystring from 'query-string'
import aqe from '@pushly/aqe'
import { axiosFetch, isAxiosResponseError } from '@halo/_utils/axios'
import { simpleNotification } from '@halo/_utils/notifications'
import { IApiCallOptions } from '@pushly/aqe/lib/interfaces'
import { FeedArticleNotificationDto } from '@halo/dtos/feed-notification.dto'
import { FeedDto } from '@halo/dtos/feed.dto'
import { FeedNotificationPreviewDto } from '@halo/dtos/feed-notification-preview-dto'
import { IFeedPreviewApiResponse } from '@halo/interfaces/feed-preview-api-response'
import { FeedConfiguration } from '@pushly/models/lib/structs/feeds/feed-configuration'
import { Feed, MutableFeed } from '@pushly/models/lib/structs/feeds/feed'
import { FeedType } from '@pushly/models/lib/enums/feed-type'
import { FeedResponse } from '@pushly/models/lib/structs/feeds/feed-response'
import { FeedNotification } from '@pushly/models/lib/structs/feeds/feed-notification'

@Singleton
export class FeedService {
    private appState: AppState
    private appService: AppService

    public constructor() {
        this.appState = Container.get(AppState)
        this.appService = Container.get(AppService)
    }

    public async create(domainId: number, createDto: any, opts: IApiCallOptions = {}): Promise<IApiResponse<FeedDto>> {
        let ok = false
        let feed: FeedDto

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: createDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = FeedDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully created.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed }
    }

    public async patchFeed(
        domainId: number,
        feedId: number,
        updateDto: MutableFeed | { enabled: boolean },
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<FeedDto>> {
        let ok = false
        let feed: FeedDto

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const req = await axiosFetch(
                'patch',
                {
                    url: serviceURL,
                    data: 'serialize' in updateDto ? updateDto.serialize({ exposeUnsetFields: false }) : updateDto,
                },
                opts.cancellationKey,
            )

            ok = true
            feed = FeedDto.fromApiResponse(req.data.data)

            simpleNotification('success', `Feed '${feed.name}' has been successfully updated.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feed }
    }

    public async archiveFeed(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<boolean>> {
        let ok = false

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const req = await axiosFetch(
                'delete',
                {
                    url: serviceURL,
                },
                opts.cancellationKey,
            )

            ok = true

            simpleNotification('success', `Feed has been successfully archived.`)
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok }
    }

    public async fetchFeedById(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<{ data: Feed; meta: any; ok: boolean }> {
        let ok = false
        let feed: Feed = new MutableFeed()
        let meta: any

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )

            feed = res.data.data
            ok = true
            meta = res.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        return { ok, data: feed, meta }
    }

    public async fetchDomainFeeds(domainId: number, opts: any): Promise<IApiResponse<FeedResponse[]>> {
        let ok = false
        let feeds: any[]
        let meta: any

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds?${options}`

        try {
            const req = await axiosFetch('get', {
                url: serviceURL,
            })

            const { data, ...responseMeta } = req.data
            ok = true
            feeds = data.map((feed: FeedResponse) => FeedResponse.parseJsonObject(feed))
            meta = responseMeta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: feeds, meta }
    }

    public async fetchFeedNotifications(
        domainId: number,
        feedId: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<FeedNotification[]>> {
        let ok = false
        let notifications: FeedNotification[] = []
        let meta: any = {}

        if (opts.showLoadingScreen) {
            this.appService.setModuleLoading()
        }

        const options = querystring.stringify(opts.query || {})
        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/${feedId}/articles?${options}`

        try {
            const res = await axiosFetch(
                'get',
                {
                    url: serviceURL,
                },
                opts?.cancellationKey,
            )

            ok = true
            const { data, ...responseMeta } = res.data
            notifications = data.map((responseData: any) => FeedNotification.parseJsonObject(responseData))
            meta = responseMeta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        if (opts.showLoadingScreen) {
            this.appService.unsetModuleLoading()
        }

        return { ok, data: notifications, meta }
    }

    public async fetchFeedPreview(
        domainId: number,
        feedConfig: FeedConfiguration,
        url: string,
        feedType: FeedType,
        numOfArticles: number,
        opts: IApiCallOptions = {},
    ): Promise<IApiResponse<IFeedPreviewApiResponse>> {
        let ok = false
        let data: IFeedPreviewApiResponse
        let meta: any

        const serviceURL = `${aqe.defaults.publicApiDomain}/v4/domains/${domainId}/feeds/preview?numOfArticles=${numOfArticles}`

        try {
            const req = await axiosFetch(
                'post',
                {
                    url: serviceURL,
                    data: {
                        previewConfig: {
                            domain_id: domainId,
                            feed_config: feedConfig.serialize(),
                            url,
                            feed_type: feedType,
                        },
                    },
                },
                opts.cancellationKey,
            )

            ok = true
            const notifData = req.data.data?.notifications.map(FeedNotificationPreviewDto.fromApiResponse)
            const estDeliveryTimeData = req.data.data?.estimated_delivery_times
            data = {
                notifications: notifData,
                estimated_delivery_times: estDeliveryTimeData,
            }
            meta = req.data.meta
        } catch (error) {
            if (isAxiosResponseError(error)) {
                simpleNotification('error', error.response.data.message)
            }
        }

        return { ok, data, meta }
    }
}
